import { useTranslation } from 'next-i18next/pages';
import { ReactNode, useMemo, useState, useRef, useEffect } from 'react';

import { useGetActiveScreenBreakpoint } from '@pflegia/ui/hooks/use-get-active-screen-breakpoint';
import Button from '@pflegia/ui/shared/button';

const FULL_WIDTH = 100;
const SWIPE_THRESHOLD = 50;
const CLICK_THRESHOLD = 10;

const MobileCarousel = ({ elements }: { elements: ReactNode[] }) => {
  const { t } = useTranslation('common');
  const [slide, setSlide] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const { isMobile } = useGetActiveScreenBreakpoint();

  const slideSize = useMemo(() => (isMobile ? 1 : 2), [isMobile]);
  const totalSlides = useMemo(() => (isMobile ? elements.length : elements.length - 1), [isMobile, elements.length]);

  const handleDotClick = (slideIndex: number) => setSlide(Math.min(slideIndex, totalSlides - 1));

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) return null;

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
    touchEndX.current = event.touches[0].clientX;
  };

  const handleTouchMove = (event: React.TouchEvent) => (touchEndX.current = event.touches[0].clientX);

  const handleTouchEnd = () => {
    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) < CLICK_THRESHOLD) return;

    if (distance > SWIPE_THRESHOLD) setSlide(prev => Math.min(prev + 1, totalSlides - 1));
    else if (distance < -SWIPE_THRESHOLD) setSlide(prev => Math.max(prev - 1, 0));
  };

  return (
    <div
      className='flex w-full flex-col justify-center'
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}>
      <div className='flex w-full items-stretch'>
        <div className='w-full'>
          <div
            className='flex gap-4 transition-transform duration-500 ease-in-out'
            style={{
              transform: `translateX(calc(-${slide * (FULL_WIDTH / slideSize)}% - ${slide} * 4 * var(--spacing)))`,
            }}>
            {elements.map(element => element)}
          </div>
        </div>
      </div>
      <div className='my-6 flex justify-center space-x-2'>
        {Array.from({ length: totalSlides }).map((_, dotIndex) => (
          <Button
            // eslint-disable-next-line react/no-array-index-key
            key={`slide-dot-${dotIndex}`}
            data-testid={`slide-dot-${dotIndex}`}
            aria-label={t('goToSlide', { number: dotIndex + 1 })}
            aria-current={dotIndex === slide ? 'true' : undefined}
            onClick={() => handleDotClick(dotIndex)}
            className='group flex h-6! w-6! items-center justify-center rounded-full bg-transparent p-1.5! shadow-none! hover:shadow-none!'>
            <span
              className={`block h-4 w-4 rounded-full transition-colors duration-500 ease-in-out ${
                dotIndex === slide ? 'bg-blue-600' : 'bg-blue-100 group-hover:bg-gray-400'
              }`}
            />
          </Button>
        ))}
      </div>
    </div>
  );
};

export default MobileCarousel;
